import GameScene from "../scenes/GameScene";
import { LevelData } from "../scenes/LevelSelectScene";
import { TiledMap } from "../utils/TiledMap";
import { Flag } from "./Flag";
import { Goal } from "./Goal";
import { Obstacle } from "./Obstacle";
import { Ramp } from "./Ramp";
import { Star } from "./Star";

export class ChunkLoader {
	constructors: { [type: string]: (x: number, y: number) => void } = {
		obstacle: this.createObstacle,
		flag: this.createFlag,
		ramp: this.createRamp,
		star: this.createStar,
	};

	scene: GameScene;
	level: LevelData;
	xPosition: number;
	numChunks = 0;
	readonly startX = 1200;
	readonly scaleRatio = 220 / 100; // ratio of tile size in game to tile size in Tiled

	tutorial: boolean;
	tutorialLevel = 0;
	private static playerLaunched = false;

	constructor(scene: GameScene, level: LevelData) {
		this.scene = scene;
		this.level = level;
		this.xPosition = this.startX;
	}

	update(): void {
		const screenDistance = this.scene.viewport.width > 1920 ? this.scene.viewport.width : 1920;
		const right = this.scene.cameras.main.scrollX + screenDistance;
		if (right + 800 > this.xPosition) {
			if (!this.tutorial) this.loadChunk();
			else if (this.tutorial) this.loadTutorialChunk();
		}
	}

	loadTutorialChunk(): void {
		const chunks = this.scene.cache.json.get("chunks") as { [key: string]: string[] };
		const ourChunks: string[] = [];
		ourChunks.push(...chunks["tutorial"]);
		const chunk = this.scene.cache.json.get(ourChunks[this.tutorialLevel]) as TiledMap;
		for (const layer of chunk.layers) {
			if (layer.type != "objectgroup") return;
			for (const obj of layer.objects) {
				const x = this.xPosition + obj.x * this.scaleRatio;
				const lane = obj.y <= 100 ? 0 : 1;
				const y = lane == 0 ? this.scene.upperLaneY : this.scene.lowerLaneY;
				this.constructors[obj.type].call(this, x, y);
			}
		}
		const chunkWidth = chunk.width * chunk.tilewidth * this.scaleRatio;
		this.xPosition += chunkWidth;
		this.scene.tutorialChunkCount++;
	}

	loadChunk(): void {
		if (this.numChunks >= this.level.length) this.createFinalChunk();

		const chunks = this.scene.cache.json.get("chunks") as { [key: string]: string[] };
		const ourChunks: string[] = [];
		for (const category of this.level.chunks) {
			ourChunks.push(...chunks[category]);
		}

		const chunk = this.scene.cache.json.get(Phaser.Math.RND.pick(ourChunks)) as TiledMap;

		for (const layer of chunk.layers) {
			if (layer.type != "objectgroup") return;
			for (const obj of layer.objects) {
				const x = this.xPosition + obj.x * this.scaleRatio;
				const lane = obj.y <= 100 ? 0 : 1;
				const y = lane == 0 ? this.scene.upperLaneY : this.scene.lowerLaneY;
				this.constructors[obj.type].call(this, x, y);
			}
		}

		const chunkWidth = chunk.width * chunk.tilewidth * this.scaleRatio;
		this.xPosition += chunkWidth;
		this.numChunks++;
	}

	createFinalChunk(): void {
		const x = this.xPosition;
		const mid = (this.scene.upperLaneY + this.scene.lowerLaneY) / 2;
		const goal = new Goal(this.scene, x, mid);
		this.scene.addObject(goal, "game");
		this.xPosition += 999999;
	}

	createObstacle(x: number, y: number): void {
		const obj = new Obstacle(this.scene, x, y);
		this.scene.addObject(obj, "game");
	}

	createFlag(x: number, y: number): void {
		const obj = new Flag(this.scene, x, y);
		this.scene.addObject(obj, "game");
	}

	createStar(x: number, y: number): void {
		const obj = new Star(this.scene, x, y);
		this.scene.addObject(obj, "game");
	}

	createRamp(x: number, y: number): void {
		const obj = new Ramp(this.scene, x, y, !ChunkLoader.playerLaunched);
		this.scene.addObject(obj, "game");
	}

	onPlayerLaunched(): void {
		ChunkLoader.playerLaunched = true;
		const ramps = this.scene.objects.filter((obj) => obj instanceof Ramp) as Ramp[];
		for (const ramp of ramps) {
			ramp.fadeArrow();
		}
	}
}
